import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchAddCampaignConcept, fetchEditCampaignConcept } from 'api/campaignArtApi';
import { submitCustomerArtBriefGraphQL, updateCustomerArtBriefGraphQL } from 'api/graphql/campaignArtGraphQL';
import { transformToArtBriefInput, transformToUpdateArtBriefInput } from 'services/campaignArtGraphQLServices';
import { artBriefRequestTypes } from 'constants/campaignConstants';
import showSnackbarError from 'utils/showSnackbarError';

const initialState = {
  artId: null,
  campaignId: '',
  concept: [],
  conceptType: '2',
  images: [],
  text: '',
  colors: '',
  phone: '',
  email: '',
  brand: '',
  website: '',
  facebook: '',
  instagram: '',
  twitter: '',
  tiktok: '',
  snapchat: '',
  phonePosition: 'Top',
  emailPosition: 'Top',
  websitePosition: 'Top',
  facebookPosition: 'Top',
  instagramPosition: 'Top',
  twitterPosition: 'Top',
  tiktokPosition: 'Top',
  snapchatPosition: 'Top',
  isLoading: false
};

export const createArtBrief = createAsyncThunk(
  'newArtBrief/createArtBrief',
  async ({ uploads }, { getState, dispatch, rejectWithValue }) => {
    try {
      const {
        artId,
        campaignId,
        text,
        colors,
        concept,
        conceptType,
        phone,
        email,
        brand,
        website,
        facebook,
        instagram,
        twitter,
        tiktok,
        snapchat,
        phonePosition,
        emailPosition,
        websitePosition,
        facebookPosition,
        instagramPosition,
        twitterPosition,
        tiktokPosition,
        snapchatPosition
      } = getState().newArtBrief;

      // Helper function to concatenate value with position for backend
      const combineValueWithPosition = (value, position) => {
        if (!value) return null;
        return `${value} - ${position || 'Top'}`;
      };

      const artBriefData = {
        uploads,
        text: `${artBriefRequestTypes[conceptType]}. ${text}`,
        colors,
        concept,
        phone: combineValueWithPosition(phone, phonePosition),
        email: combineValueWithPosition(email, emailPosition),
        brand,
        website: combineValueWithPosition(website, websitePosition),
        facebook: combineValueWithPosition(facebook, facebookPosition),
        instagram: combineValueWithPosition(instagram, instagramPosition),
        twitter: combineValueWithPosition(twitter, twitterPosition),
        tiktok: combineValueWithPosition(tiktok, tiktokPosition),
        snapchat: combineValueWithPosition(snapchat, snapchatPosition)
      };

      if (artId) {
        // Update existing art brief via GraphQL
        const input = transformToUpdateArtBriefInput(artBriefData, campaignId, artId);
        await updateCustomerArtBriefGraphQL(input);
      } else {
        // Create new art brief via GraphQL
        const input = transformToArtBriefInput(artBriefData, campaignId);
        const result = await submitCustomerArtBriefGraphQL(input);
        
        if (!result.success) {
          throw new Error(result.error || result.message || 'Failed to submit art brief');
        }
      }

      return null;
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to save art brief';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const newArtBriefSlice = createSlice({
  name: 'newArtBrief',
  initialState,
  reducers: {
    setBriefConcept(state, action) {
      state.concept = action.payload.concept;
    },
    setBriefConceptType(state, action) {
      state.conceptType = action.payload.conceptType;
    },
    setBriefImages(state, action) {
      state.images = action.payload.images;
    },
    setBriefInfo(state, action) {
      state.campaignId = action.payload.campaignId;
    },
    setBriefText(state, action) {
      state.text = action.payload.text;
      state.colors = action.payload.colors;
      state.phone = action.payload.phone;
      state.email = action.payload.email;
      state.brand = action.payload.brand;
      state.website = action.payload.website;
      state.facebook = action.payload.facebook;
      state.instagram = action.payload.instagram;
      state.twitter = action.payload.twitter;
      state.tiktok = action.payload.tiktok;
      state.snapchat = action.payload.snapchat;
      // Store positions separately if provided
      if (action.payload.phonePosition !== undefined) state.phonePosition = action.payload.phonePosition;
      if (action.payload.emailPosition !== undefined) state.emailPosition = action.payload.emailPosition;
      if (action.payload.websitePosition !== undefined) state.websitePosition = action.payload.websitePosition;
      if (action.payload.facebookPosition !== undefined) state.facebookPosition = action.payload.facebookPosition;
      if (action.payload.instagramPosition !== undefined) state.instagramPosition = action.payload.instagramPosition;
      if (action.payload.twitterPosition !== undefined) state.twitterPosition = action.payload.twitterPosition;
      if (action.payload.tiktokPosition !== undefined) state.tiktokPosition = action.payload.tiktokPosition;
      if (action.payload.snapchatPosition !== undefined) state.snapchatPosition = action.payload.snapchatPosition;
    },
    setBriefDefaultFields(state, action) {
      state.artId = action.payload.artId;
      state.concept = action.payload.concept;
      state.campaignId = action.payload.campaignId;
      state.text = action.payload.text || '';
      state.images = action.payload.images || [];
      state.colors = action.payload.colors || '';
      state.conceptType = action.payload.conceptType;
      state.phone = action.payload.phone || '';
      state.email = action.payload.email || '';
      state.brand = action.payload.brand || '';
      state.website = action.payload.website || '';
      state.facebook = action.payload.facebook || '';
      state.instagram = action.payload.instagram || '';
      state.twitter = action.payload.twitter || '';
      state.tiktok = action.payload.tiktok || '';
      state.snapchat = action.payload.snapchat || '';
      // Set position fields
      state.phonePosition = action.payload.phonePosition || 'Top';
      state.emailPosition = action.payload.emailPosition || 'Top';
      state.websitePosition = action.payload.websitePosition || 'Top';
      state.facebookPosition = action.payload.facebookPosition || 'Top';
      state.instagramPosition = action.payload.instagramPosition || 'Top';
      state.twitterPosition = action.payload.twitterPosition || 'Top';
      state.tiktokPosition = action.payload.tiktokPosition || 'Top';
      state.snapchatPosition = action.payload.snapchatPosition || 'Top';
    },
    clearNewArtBrief(state) {
      state.artId = null;
      state.campaignId = '';
      state.concept = [];
      state.conceptType = '2';
      state.images = [];
      state.text = '';
      state.colors = '';
      state.phone = '';
      state.email = '';
      state.brand = '';
      state.website = '';
      state.facebook = '';
      state.instagram = '';
      state.twitter = '';
      state.tiktok = '';
      state.snapchat = '';
      // Reset position fields
      state.phonePosition = 'Top';
      state.emailPosition = 'Top';
      state.websitePosition = 'Top';
      state.facebookPosition = 'Top';
      state.instagramPosition = 'Top';
      state.twitterPosition = 'Top';
      state.tiktokPosition = 'Top';
      state.snapchatPosition = 'Top';
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createArtBrief.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createArtBrief.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createArtBrief.rejected, (state) => {
      state.isLoading = false;
    });
  }
});

export const {
  setBriefConcept,
  setBriefConceptType,
  setBriefImages,
  setBriefInfo,
  setBriefText,
  setBriefDefaultFields,
  setUploads,
  clearNewArtBrief
} = newArtBriefSlice.actions;

export default newArtBriefSlice.reducer;
